import { useCallback, useContext, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { isRewardFareClass, fareClassToUrlMap, parseFareClassFromUrl, type FareClassWithReward } from 'constants/flight'
import { getCharterFareCabinLabel } from 'hooks/Flights/useCharterFlightLabels'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import OfferFilterDropdownBox from '../OfferFilterDropdownBox'
import LineSeatIcon from 'LuxKit/Icons/line/LineSeatIcon'
import ModalContext from 'contexts/ModalContext'
import MaldivesCharterFlightFareClassModal from './MaldivesCharterFlightFareClassModal'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useCharterFareClassPolicy from 'hooks/Flights/useCharterFareClassPolicy'
import useMaldivesCharterModalFareClasses from 'hooks/Flights/useMaldivesCharterModalFareClasses'
import { parseAsString, useQueryState } from 'nuqs'

interface Props {
  offer?: App.Offer
  fareClass: FareClassWithReward
  className?: string
  checkInDate?: string
  checkOutDate?: string
  occupancies: Array<App.Occupants>
}

function CharterFlightFareClassSelect({ offer, fareClass, className, checkInDate, checkOutDate, occupancies }: Props) {
  const intl = useIntl()
  const openModal = useContext(ModalContext)
  const dispatch = useOfferPageDispatch()
  const [fareClassUrlRaw, setFareClassUrl] = useQueryState('fareClass', parseAsString)
  const fareClassFromUrl = fareClassUrlRaw ? parseFareClassFromUrl(fareClassUrlRaw) : undefined
  const setFareClassWithUrl = useCallback((fc: FareClassWithReward) => {
    dispatch({ type: OfferPageActions.setFareClass, fareClass: fc })
    setFareClassUrl(fareClassToUrlMap[fc])
  }, [dispatch, setFareClassUrl])

  // Sync URL → offer page state when the calendar or another component changes the URL
  useEffect(() => {
    if (fareClassFromUrl && fareClassFromUrl !== fareClass) {
      dispatch({ type: OfferPageActions.setFareClass, fareClass: fareClassFromUrl })
    }
  }, [fareClassFromUrl, fareClass, dispatch])

  const charterFareClassPolicy = useCharterFareClassPolicy(offer, occupancies)
  const allowedFareClassesForModal = useMaldivesCharterModalFareClasses(charterFareClassPolicy.allowedFareClasses)
  const canOpenModal = allowedFareClassesForModal.length > 1

  if (!canOpenModal) {
    return null
  }

  const openFareClassModal = async() => {
    const selectedClass = await openModal<FareClassWithReward>(<MaldivesCharterFlightFareClassModal
      selectedClassId={fareClass}
      checkInDate={checkInDate}
      checkOutDate={checkOutDate}
      occupancies={occupancies}
      allowedFareClasses={allowedFareClassesForModal}
    />)
    if (selectedClass) {
      setFareClassWithUrl(selectedClass)
    }
  }

  const cabinLabel = getCharterFareCabinLabel(intl, fareClass)
  const value = isRewardFareClass(fareClass)
    ? <b>{intl.formatMessage(
        {
          id: 'jnxEIPY1CCyg-STqwYK9',
          defaultMessage: '{cabin} {loyaltyProgramName} Rewards',
          description: 'Charter flight fare class selector value when a reward variant is selected; cabin is the base cabin name (Economy, Economy Plus, Business), loyaltyProgramName is the configured loyalty program (e.g. Société).',
        },
        { cabin: cabinLabel, loyaltyProgramName: getLoyaltyProgramDisplayName() },
      )}
    </b>
    : <b>{cabinLabel}</b>

  return (
    <OfferFilterDropdownBox
      className={className}
      data-testid="fare-class-select"
      onClick={openFareClassModal}
      icon={<LineSeatIcon />}
      label={intl.formatMessage({
        id: 'qziWiR2Ojnm1pUMoGMst',
        defaultMessage: 'Class',
        description: 'Label for charter flight fare class selector in the flights flow',
      })}
      value={value}
      placeholder={intl.formatMessage({
        id: 's-t6Cr_PH8dkvIHHzzGD',
        defaultMessage: 'Select fare class',
        description: 'Placeholder text for charter flight fare class selector',
      })}
    />
  )
}

type MappedProps = Pick<Props, 'offer' | 'fareClass' | 'checkInDate' | 'checkOutDate' | 'occupancies'>

export default withOfferPageState((state): MappedProps => ({
  offer: state.offer,
  fareClass: state.fareClass,
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
  occupancies: state.rooms,
}), CharterFlightFareClassSelect)
